import {
  Avatar,
  Box,
  Grid,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Button,
  Tooltip
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { WarningAmberRounded, Lock } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { nivelUsuarioEnum } from 'shared/Enums';
import ProjetosEnum from 'shared/Enums/ProjetosEnum';
import { montaUrlExterna, parseJwt } from 'api/authentication';
import {
  exibeValorLocalizado,
  exibeValorResumido,
  mascararCpfCnpj,
  getConfigAmbiente,
  getToken,
  criaCookie,
  AUTH_COOKIE_TOKEN,
  geraMD5Token,
  VISAOCLIENTE_CLIENTE_INFOS
} from 'shared/services/User';
import { getConfigAmbienteDominio } from '../../../../../utils/tenant';
import CorrenteFechadaIcon from '../../../../../assets/img/corrente_fechada.svg';
import EstudoClienteIcon from '../../../../../assets/img/estudo_cliente.svg';
import { retornaInformacoesNasPillsDeCheckpoints } from '../../../../Onboarding/shared/Utils';
import './styles.scss';

const formatarValorMonetario = (params, link = false) => {
  var valor = params.value && parseInt(params.value) !== 0 ? parseInt(params.value) : 0;
  let negativa = false;
  if (valor < 0) {
    valor = parseFloat(String(valor).replace('-', ''));
    negativa = true;
  }
  return (
    <>
      {valor !== 0 ? (
        <Box className="texto-nowrap-overflow-dotted">
          <span
            title={'R$ ' + (negativa ? '-' : '') + exibeValorLocalizado(valor)}
            className={negativa ? 'texto-danger' : ''}
          >
            {(negativa ? '-' : '') + exibeValorResumido(valor)}
          </span>
        </Box>
      ) : (
        <Box>
          <HorizontalRuleIcon className={(link ? 'btnCampoSemValor ' : '') + 'texto-cor-cinza-claro'} />
        </Box>
      )}
    </>
  );
};

const DiasVencimentoCell = React.memo(({ vencimento }) => {
  const { tipo, diasVencimentoProximo, lstMsgVencimento } = vencimento;
  const className = useMemo(() => {
    if (tipo === 'vencido') return 'texto-danger';
    if (tipo === 'vai_vencer') return 'texto-warning';
    return '';
  }, [tipo]);

  const title = useMemo(() => lstMsgVencimento.join('\n'), [lstMsgVencimento]);
  return (
    <Box className={className} title={title} disableElevation disableRipple size="small">
      {diasVencimentoProximo}
    </Box>
  );
});

const limiteRiscoDisponivel = (params, coluna, dadosUser) => {
  var valor = params.value && parseInt(params.value) !== 0 ? parseInt(params.value) : 0;
  let negativa = false;
  if (valor < 0) {
    valor = parseFloat(String(valor).replace('-', ''));
    negativa = true;
  }
  const statusList =
    coluna !== 'risco' && params.row && Array.isArray(params.row.infoProdutosTotal.status)
      ? params.row.infoProdutosTotal.status
      : [];

  const msgsBloqueado = statusList
    .filter(s => s.tipoStatus === 'bloqueado')
    .flatMap(s => s.lstMsgProdutos);

  const hasBloqueado = msgsBloqueado.length > 0;

  return (
    <>
      {valor !== 0 ? (
        <Box className="texto-nowrap-overflow-dotted">
          {hasBloqueado &&
            coluna === 'limite' &&
            dadosUser &&
            dadosUser.nivel !== nivelUsuarioEnum.CLIENTE && (
              <span title={msgsBloqueado.join('\n')}>
                <Lock className="texto-danger ajustar-icone-direita bloqueado" />
              </span>
            )}
          <span
            title={`R$ ${(negativa ? '-' : '') + exibeValorLocalizado(valor)}`}
            className={negativa ? 'texto-danger' : ''}
          >
            {(negativa ? ' -' : ' ') + exibeValorResumido(valor)}
          </span>
        </Box>
      ) : (
        <Box>
          <HorizontalRuleIcon className="texto-cor-cinza-claro" />
        </Box>
      )}
    </>
  );
};

const obterTituloMedia = (dados, tipo, produto) => {
  var titulo = '';
  if (dados && dados.dadosMedias && dados.dadosMedias[tipo]) {
    if (
      produto !== 'total' &&
      dados.dadosMedias[tipo].mediaProdutos &&
      dados.dadosMedias[tipo].mediaProdutos[produto]
    ) {
      titulo = 'A media é de R$ ' + exibeValorLocalizado(dados.dadosMedias[tipo].mediaProdutos[produto]);
    } else if (produto === 'total' && dados.dadosMedias[tipo].mediaTotal) {
      titulo = 'A media total é de R$ ' + exibeValorLocalizado(dados.dadosMedias[tipo].mediaTotal);
    }

    if (produto !== 'total' && dados.dadosMedias[tipo].qtd && dados.dadosMedias[tipo].qtd[produto]) {
      titulo += ' referente a ' + dados.dadosMedias[tipo].qtd[produto] + ' clientes.';
    } else if (produto === 'total' && dados.dadosMedias[tipo].qtdTotal) {
      titulo += ' referente a ' + dados.dadosMedias[tipo].qtdTotal + ' clientes.';
    }
  }
  return titulo;
};

const ActionsCell = ({
  params,
  clickCheckpoints,
  clickParecer,
  clickLimiteComercial,
  clickPipeComercial,
  clickVOP,
  clickStatus
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCheckpointClick = () => {
    clickCheckpoints(params.row);
    handleClose();
  };

  const handleParecerClick = () => {
    clickParecer(params.row);
    handleClose();
  };

  const handleLimiteComercialClick = () => {
    clickLimiteComercial(params.row);
    handleClose();
  };

  const handlePipeComercialClick = () => {
    clickPipeComercial(params.row);
    handleClose();
  };

  const handleVOPClick = () => {
    clickVOP(params.row);
    handleClose();
  };

  return (
    <Box className="btnAcoesGrid">
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuItem onClick={handleCheckpointClick}>Checkpoint de negócios</MenuItem>
        <MenuItem onClick={handleParecerClick}>Parecer</MenuItem>
        <MenuItem onClick={handleLimiteComercialClick}>Limite comercial</MenuItem>
        <MenuItem onClick={handlePipeComercialClick}>Pipe comercial</MenuItem>
        <MenuItem onClick={handleVOPClick}>VOP Mensal</MenuItem>
      </Menu>

      <Box
        className="statusGrid"
        sx={{
          flexDirection: { xs: 'column', sm: 'row' }
        }}
      >
        {params.row.status && params.value && ['danger', 'warning'].includes(params.value) && (
          <IconButton onClick={() => clickStatus(params.row)} className="pad-0">
            <WarningAmberRounded className={`texto-${params.value}`} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export const definicaoDeColunas = ({
  clickCheckpoints,
  clickLimiteComercial,
  clickPipeComercial,
  clickVOP,
  clickGrupoEconomico,
  clickStatus,
  clickParecer,
  filtroDashboard,
  clickExibirProdutos,
  dadosUser,
  expandirRetrairGrid,
  dadosAdicionaisGrid,
  clienteAtual,
  produtos
}) => {
  var configColumns = [
    {
      field: 'nomeCliente',
      headerName: 'Empresa',
      minWidth: 80,
      headerAlign: 'left',
      headerClassName: 'data-grid-light-blue border-2-right-white font-bold-header items-esquerda',
      cellClassName:
        ((dadosUser && dadosUser.nivel === nivelUsuarioEnum.CLIENTE) ||
        (filtroDashboard && filtroDashboard.gerente && filtroDashboard.gerente !== 'todos') ||
        (clienteAtual && clienteAtual.id !== 'todos')
          ? 'pad-left-0 '
          : '') + 'data-grid-pipe-light-gray border-2-right-white items-esquerda',
      sortComparator: (v1, v2) => v1.localeCompare(v2, 'pt-BR'),
      sortingOrder: ['asc', 'desc'],
      renderCell: params =>
        criarClienteNomeCnpj({ params, clickGrupoEconomico, dadosUser, filtroDashboard }),
      getQuickFilterValue: function(params) {
        // pega o nome “limpo”
        const rawNome = params.value || '';
        const nome = rawNome.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

        // pega o CNPJ “limpo” (só dígitos)
        const rawCnpj = params.row.cnpjCliente || '';
        const cnpj = rawCnpj.replace(/[^0-9]/g, '').toLowerCase();

        // devolve nome + espaço + CNPJ
        return nome + ' ' + cnpj;
      }
    },
    {
      field: 'porteCliente',
      headerName: 'EMP',
      headerAlign: 'center',
      minWidth: 80,
      headerClassName: 'data-grid-light-blue border-2-right-white font-bold-header',
      cellClassName: 'font-size-0-8-rem data-grid-pipe-light-gray border-2-right-white items-centro',
      sortComparator: (v1, v2) => v1 - v2,
      sortingOrder: ['asc', 'desc'],
      renderHeader: () => (
        <Tooltip placement="top" title={<>Porte da empresa</>}>
          <Box className="texto-nowrap-overflow-dotted">EMP</Box>
        </Tooltip>
      ),
      renderCell: params => {
        return (
          <>
            {params.value !== null ? (
              <Box>
                <Box>{`${params.value !== null ? params.value : ''}`}</Box>
              </Box>
            ) : (
              <Box>
                <HorizontalRuleIcon className="texto-cor-cinza-claro" />
              </Box>
            )}
          </>
        );
      }
    },
    {
      field: 'qtdProduto',
      headerName: 'Prod.',
      headerAlign: 'center',
      minWidth: 80,
      headerClassName: 'data-grid-light-blue border-2-right-white font-bold-header',
      cellClassName: 'font-size-0-8-rem data-grid-pipe-light-gray border-2-right-white items-centro',
      sortComparator: (v1, v2) => v1 - v2,
      sortingOrder: ['asc', 'desc'],
      renderHeader: () => (
        <Tooltip
          placement="top"
          title={
            <>
              Quantidade de produtos.
              <br />
              Clique no número de produtos abaixo para exibir mais informações.
            </>
          }
        >
          <Box className="texto-nowrap-overflow-dotted">Prod.</Box>
        </Tooltip>
      ),
      renderCell: params => {
        return (
          <Button
            className="btn-qtd-produtos"
            onClick={params.value > 0 ? () => clickExibirProdutos(params.row) : undefined}
            disabled={!params.value || params.value <= 0}
          >
            {params.value}
          </Button>
        );
      }
    },
    {
      field: 'diasVencimento',
      headerName: 'Vencimento',
      headerAlign: 'center',
      minWidth: 80,
      headerClassName: 'data-grid-light-blue border-2-right-white font-bold-header',
      cellClassName: 'font-size-0-8-rem data-grid-pipe-light-gray border-2-right-white items-centro',
      sortComparator: (v1, v2) => v1 - v2,
      sortingOrder: ['asc', 'desc'],
      renderHeader: () => (
        <Tooltip
          className="toolTipVencimento"
          placement="top"
          title={
            <>
              Quantidade de dias do produto mais próximo ao vencimento.
              <br />
              Passe o mouse sobre a quantidade de dias para mais detalhes.
            </>
          }
        >
          <Box className="texto-nowrap-overflow-dotted">Venc.</Box>
        </Tooltip>
      ),
      valueGetter: params => params.row.infoProdutosTotal.vencimento.diasVencimentoProximo,
      renderCell: params => <DiasVencimentoCell vencimento={params.row.infoProdutosTotal.vencimento} />
    },
    {
      field: 'limite_credito',
      headerName: 'Lim. Créd.',
      minWidth: 80,
      headerAlign: 'center',
      headerClassName: 'data-grid-light-blue font-bold-header',
      cellClassName: 'data-grid-pipe-light-gray items-centro',
      sortComparator: (v1, v2) => parseFloat(v1) - parseFloat(v2),
      sortingOrder: ['desc', 'asc'],
      valueGetter: params => params.row.infoProdutosTotal.limite_credito,
      renderCell: params => limiteRiscoDisponivel(params, 'limite', dadosUser),
      valueFormatter: params => parseInt(params.value),
      renderHeader: () => (
        <Tooltip placement="top" title={<>Limite de Crédito Total</>}>
          <Box className="texto-nowrap-overflow-dotted">Lim. Créd.</Box>
        </Tooltip>
      )
    },
    {
      field: 'risco',
      headerName: 'Risco',
      minWidth: 80,
      headerAlign: 'center',
      headerClassName: 'data-grid-light-blue font-bold-header',
      cellClassName: 'data-grid-pipe-light-gray items-centro',
      description: obterTituloMedia(dadosAdicionaisGrid, 'risco', 'normal'),
      sortComparator: (v1, v2) => parseFloat(v1) - parseFloat(v2),
      sortingOrder: ['desc', 'asc'],
      valueGetter: params => params.row.infoProdutosTotal.risco,
      renderCell: params => limiteRiscoDisponivel(params, 'risco', dadosUser),
      valueFormatter: params => parseInt(params.value)
    },
    {
      field: 'saldo_credito',
      headerName: 'Saldo Créd.',
      headerAlign: 'center',
      minWidth: 80,
      headerClassName: 'data-grid-light-blue font-bold-header',
      cellClassName: 'data-grid-pipe-light-gray items-centro',
      sortComparator: (v1, v2) => parseFloat(v1) - parseFloat(v2),
      sortingOrder: ['desc', 'asc'],
      valueGetter: params => params.row.infoProdutosTotal.saldo_credito,
      renderCell: params => limiteRiscoDisponivel(params, 'saldo', dadosUser),
      valueFormatter: params => parseInt(params.value),
      renderHeader: () => (
        <Tooltip placement="top" title={<>Limite de Crédito Disponível</>}>
          <Box className="texto-nowrap-overflow-dotted">Saldo Créd.</Box>
        </Tooltip>
      )
    },
    {
      field: 'valorPipeComercial',
      headerName: 'Pipe com.',
      minWidth: 80,
      headerAlign: 'center',
      headerClassName: 'data-grid-light-blue font-bold border-2-right-white',
      sortComparator: (v1, v2) => {
        v1 = v1 !== '-' && v1 !== null ? parseInt(v1) : 0;
        v2 = v2 !== '-' && v2 !== null ? parseInt(v2) : 0;
        return v1 - v2;
      },
      sortingOrder: ['desc', 'asc'],
      cellClassName: `data-grid-pipe-light-gray items-centro ${
        expandirRetrairGrid ? 'border-2-right-white' : ''
      } `,
      renderCell: params => (
        <Box className="btnValorAbrirModal" onClick={() => clickPipeComercial(params.row)}>
          {formatarValorMonetario(params, true)}
        </Box>
      ),
      renderHeader: () => (
        <Tooltip placement="top" title={<>Pipe Comercial</>}>
          <Box className="texto-nowrap-overflow-dotted">Pipe com.</Box>
        </Tooltip>
      )
    },
    {
      field: 'valorVop',
      headerName: 'VOP',
      minWidth: 80,
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip placement="top" title={<>Volume operado</>}>
          <Box className="texto-nowrap-overflow-dotted">VOP</Box>
        </Tooltip>
      ),
      headerClassName: 'data-grid-light-blue font-bold border-2-right-white',
      sortComparator: (v1, v2) => {
        v1 = v1 !== '-' && v1 !== null ? parseInt(v1) : 0;
        v2 = v2 !== '-' && v2 !== null ? parseInt(v2) : 0;
        return v1 - v2;
      },
      sortingOrder: ['desc', 'asc'],
      cellClassName: `data-grid-pipe-light-gray items-centro ${
        expandirRetrairGrid ? 'border-2-right-white' : ''
      } `,
      renderCell: params => (
        <Box
          className="btnValorAbrirModal"
          onClick={() => clickVOP(params.row)}
          style={{ cursor: 'pointer' }}
        >
          {formatarValorMonetario(params, true)}
        </Box>
      )
    },
    {
      field: 'vop_a_realizar',
      headerName: 'À realizar',
      minWidth: 80,
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip placement="top" title={<>VOP à realizar</>}>
          <Box className="texto-nowrap-overflow-dotted">À realizar</Box>
        </Tooltip>
      ),
      headerClassName: 'data-grid-light-blue font-bold border-2-right-white',
      sortComparator: (v1, v2) => {
        v1 = v1 !== '-' && v1 !== null ? parseInt(v1) : 0;
        v2 = v2 !== '-' && v2 !== null ? parseInt(v2) : 0;
        return v1 - v2;
      },
      sortingOrder: ['desc', 'asc'],
      cellClassName: `data-grid-pipe-light-gray items-centro ${
        expandirRetrairGrid ? 'border-2-right-white' : ''
      } `,
      valueGetter: params => params.row.infoProdutosTotal.vop_a_realizar,
      renderCell: params => formatarValorMonetario(params)
    },
    {
      field: 'dias_ultima_operacao',
      headerName: 'Churn',
      minWidth: 80,
      headerAlign: 'center',
      headerClassName: 'data-grid-light-blue font-bold border-2-right-white',
      sortComparator: (v1, v2) => {
        v1 = v1 !== '-' && v1 !== null ? parseInt(v1) : 0;
        v2 = v2 !== '-' && v2 !== null ? parseInt(v2) : 0;
        return v1 - v2;
      },
      sortingOrder: ['desc', 'asc'],
      cellClassName: params => {
        let classes = 'data-grid-pipe-light-gray border-2-right-white items-centro ';
        if (params.value === '-') {
          classes += 'texto-cor-cinza-claro';
        } else {
          let dias = parseInt(params.value);
          if (dias <= 30) classes += 'texto-success';
          else if (dias > 30 && dias <= 120) classes += 'texto-warning';
          else if (dias > 120) classes += 'texto-danger';
        }
        return classes;
      },
      renderCell: params => {
        if (!params.value || !params.row.data_ultima_operacao) {
          return (
            <Box>
              <HorizontalRuleIcon className="texto-cor-cinza-claro" />
            </Box>
          );
        }
        return (
          <Box>
            <Box title={params.row.data_ultima_operacao}>{params.value}</Box>
          </Box>
        );
      },
      renderHeader: () => (
        <Tooltip placement="top" title={<>Quantidade de dias sem operar</>}>
          <Box className="texto-nowrap-overflow-dotted">Churn</Box>
        </Tooltip>
      )
    },
    {
      field: 'checkpoint',
      headerName: 'Checkpoint',
      headerAlign: 'center',
      minWidth: 80,
      headerClassName: 'data-grid-light-blue font-bold-header border-2-right-white',
      cellClassName: 'data-grid-pipe-light-gray items-centro',
      renderCell: params => {
        const checkPoint = params.row.checkpoint;
        if (!checkPoint || !checkPoint.descricao) {
          return (
            <Box className="cursor-pointer" onClick={() => clickCheckpoints(params.row)}>
              <HorizontalRuleIcon className="texto-cor-cinza-claro" />
            </Box>
          );
        }
        return (
          <Box
            className="texto-hidden-overflow btnCheckPoint"
            onClick={() => clickCheckpoints(params.row)}
          >
            <Typography
              variant="caption"
              className="texto-nowrap-overflow-dotted font-bold-500"
              title={retornaInformacoesNasPillsDeCheckpoints(
                checkPoint.data_insercao,
                checkPoint.descricao,
                checkPoint.usuario_insercao,
                checkPoint.observacao
              )}
            >
              {checkPoint.descricao}
            </Typography>
          </Box>
        );
      }
      // ,
      // renderHeader: () => (
      //   <Tooltip placement="top" title={<>Quantidade de dias sem operar</>}>
      //     <Box className="texto-nowrap-overflow-dotted">Churn</Box>
      //   </Tooltip>
      // )
    },
    {
      field: 'tipoStatus',
      headerName: 'Ações',
      minWidth: 80,
      headerAlign: 'center',
      headerClassName: 'data-grid-light-blue border-2-right-white font-bold-header',
      cellClassName:
        'flex-box-items-centralizados items-centralizados data-grid-pipe-light-gray border-2-right-white items-centro',
      sortable: false,
      renderCell: params => (
        <>
          <ActionsCell
            params={params}
            clickCheckpoints={clickCheckpoints}
            clickParecer={clickParecer}
            clickLimiteComercial={clickLimiteComercial}
            clickPipeComercial={clickPipeComercial}
            clickVOP={clickVOP}
            dadosUser={dadosUser}
            clickStatus={clickStatus}
          />
        </>
      )
    }
  ];

  if (
    (filtroDashboard && filtroDashboard.gerente && filtroDashboard.gerente === 'todos') ||
    (dadosUser && dadosUser.nivel === nivelUsuarioEnum.CLIENTE)
  ) {
    configColumns.unshift({
      field: 'nomeGerente',
      headerName: 'GN',
      minWidth: 80,
      headerAlign: 'left',
      headerClassName: 'data-grid-light-blue border-2-right-white font-bold-header items-esquerda',
      cellClassName: 'data-grid-pipe-light-gray border-2-right-white items-esquerda pad-left-0',
      sortComparator: (v1, v2) => v1.localeCompare(v2, 'pt-BR'),
      sortingOrder: ['asc', 'desc'],
      renderCell: params => criarGerenteAvatar(params, dadosUser)
    });
  }

  if (dadosUser && dadosUser.nivel === nivelUsuarioEnum.CLIENTE) {
    const camposAExcluir = [
      'porteCliente',
      'valorPipeComercial',
      'vop_a_realizar',
      'checkpoint',
      'tipoStatus'
    ];
    configColumns = configColumns.filter(col => !camposAExcluir.includes(col.field));
  }

  if (produtos && produtos.length > 0) {
    const dynLimiteComercial = produtos.map(prod => {
      const { codProduto: key, siglaProduto } = prod;
      const field = `limite_comercial_${siglaProduto.toLowerCase()}`;
      return {
        field,
        headerName: siglaProduto,
        headerAlign: 'center',
        flex: 1,
        headerClassName: 'data-grid-light-blue font-bold-header',
        cellClassName: 'data-grid-pipe-light-gray items-centro',
        valueGetter: params => params.row.infoProdutos[key].limite_comercial || 0,
        renderCell: params => formatarValorMonetario(params),
        sortComparator: (v1, v2) => (parseFloat(v1) || 0) - (parseFloat(v2) || 0)
      };
    });

    const dynLimiteCredito = produtos.map(prod => {
      const { codProduto: key, siglaProduto } = prod;
      const field = `limite_credito_${siglaProduto.toLowerCase()}`;
      return {
        field,
        headerName: siglaProduto,
        headerAlign: 'center',
        flex: 1,
        headerClassName: 'data-grid-light-blue font-bold-header',
        cellClassName: 'data-grid-pipe-light-gray items-centro',
        valueGetter: params => params.row.infoProdutos[key].limite_credito || 0,
        renderCell: params => formatarValorMonetario(params),
        sortComparator: (v1, v2) => (parseFloat(v1) || 0) - (parseFloat(v2) || 0)
      };
    });

    const dynDisponivel = produtos.map(prod => {
      const { codProduto: key, siglaProduto } = prod;
      const field = `saldo_credito_${siglaProduto.toLowerCase()}`;
      return {
        field,
        headerName: siglaProduto,
        headerAlign: 'center',
        flex: 1,
        headerClassName: 'data-grid-light-blue font-bold-header',
        cellClassName: 'data-grid-pipe-light-gray items-centro',
        valueGetter: params => params.row.infoProdutos[key].saldo_credito || 0,
        renderCell: params => formatarValorMonetario(params),
        sortComparator: (v1, v2) => (parseFloat(v1) || 0) - (parseFloat(v2) || 0)
      };
    });

    const dynRisco = produtos.map(prod => {
      const { codProduto: key, siglaProduto } = prod;
      const field = `risco_${siglaProduto.toLowerCase()}`;
      return {
        field,
        headerName: siglaProduto,
        headerAlign: 'center',
        flex: 1,
        headerClassName: 'data-grid-light-blue font-bold-header',
        cellClassName: 'data-grid-pipe-light-gray items-centro',
        valueGetter: params => params.row.infoProdutos[key].risco || 0,
        renderCell: params => formatarValorMonetario(params),
        sortComparator: (v1, v2) => (parseFloat(v1) || 0) - (parseFloat(v2) || 0)
      };
    });

    configColumns.push(...dynLimiteComercial, ...dynLimiteCredito, ...dynDisponivel, ...dynRisco);
  }

  return configColumns;
};

export const criarGerenteAvatar = (params, dadosUser) => {
  const mostrarSiga =
    dadosUser && dadosUser.nivel !== nivelUsuarioEnum.CLIENTE && params.row && params.row.corSiga;

  let [tooltip, color] = mostrarSiga ? params.row.corSiga.split('|') : [null, null];
  tooltip = tooltip ? tooltip.charAt(0).toUpperCase() + tooltip.slice(1).toLowerCase() : '';
  color =
    color && mostrarSiga
      ? color
      : dadosUser && dadosUser.nivel === nivelUsuarioEnum.CLIENTE
      ? '#FFFFFF'
      : false;

  let tituloGerente = params.value || '';
  if (params.row && params.row.nomePlataforma) {
    tituloGerente += `\nPlataf.: ${params.row.nomePlataforma}`;
  }
  if (params.row && params.row.nomeUnidadeNegocio) {
    tituloGerente += `\nUnd.Neg.: ${params.row.nomeUnidadeNegocio}`;
  }

  const content =
    params.value === '-' ? (
      <HorizontalRuleIcon className="texto-cor-cinza-claro" />
    ) : (
      <Typography
        title={tituloGerente}
        variant="subtitle1"
        className={
          (dadosUser && dadosUser.nivel === nivelUsuarioEnum.CLIENTE
            ? 'max-width-200 '
            : 'max-width-90 ') + 'texto-cor-preto font-size-0-7rem texto-hidden-overflow max-width-90'
        }
      >
        {params.value}
      </Typography>
    );

  return (
    <Box
      className="flex-box items-centro-verticalmente"
      title={mostrarSiga ? tooltip : undefined}
      sx={
        color !== false
          ? {
              borderLeft: `5px solid ${color}`,
              minHeight: '60px',
              borderRadius: '5px',
              paddingLeft: '5px !important'
            }
          : undefined
      }
    >
      {content}
    </Box>
  );
};

async function montarUrlEstudoCliente(codcliente) {
  const configAmbiente = getConfigAmbiente();
  const token = getToken();
  const jwtToken = parseJwt(token);

  const dominio = configAmbiente.sistema.dominio || 'one7.in';
  const expirationInSeconds = 55;
  const expirationDate = new Date(Date.now() + expirationInSeconds * 1000);

  // Equivalente ao accept-language: navigator.languages[0] geralmente é o mais aceito
  const locale =
    navigator.languages && navigator.languages.length > 0
      ? navigator.languages[0].replace('-', '_')
      : navigator.language.replace('-', '_');

  const userAgent = navigator.userAgent;

  // Use o mesmo padrão do backend: md5(id + userAgent + locale)
  const md5React = geraMD5Token(jwtToken.id, userAgent + locale);

  criaCookie(AUTH_COOKIE_TOKEN, token, dominio, expirationDate);
  criaCookie(VISAOCLIENTE_CLIENTE_INFOS, md5React, dominio, expirationDate);

  const rota = `estudo-cliente.get-index`;
  const params = `&CODCLIENTE=${codcliente}`;
  const url = montaUrlExterna(rota, ProjetosEnum.MODULUS, params);

  const dominioUrl = getConfigAmbienteDominio();
  const novaUrl = url.replace('TENANT', dominioUrl);

  window.open(novaUrl, '_blank');
}

export const criarClienteNomeCnpj = ({ params, clickGrupoEconomico, dadosUser, filtroDashboard }) => {
  const mostrarSiga =
    dadosUser &&
    dadosUser.nivel !== nivelUsuarioEnum.CLIENTE &&
    filtroDashboard &&
    filtroDashboard.gerente &&
    filtroDashboard.gerente !== 'todos';

  let [tooltip, color] = mostrarSiga ? params.row.corSiga.split('|') : [null, null];
  tooltip =
    tooltip && mostrarSiga ? tooltip.charAt(0).toUpperCase() + tooltip.slice(1).toLowerCase() : '';
  color = color && mostrarSiga ? color : false;

  if (params.value === 'Total geral') {
    return (
      <Typography component="div" className="font-bold pad-topbot-20">
        Total geral
      </Typography>
    );
  }

  return (
    <Box className="texto-nowrap-overflow-dotted campoGrupoEconomicoGrid pad-left-0">
      <Box
        title={tooltip || undefined}
        sx={
          color !== false
            ? {
                borderLeft: `5px solid ${color}`,
                minHeight: '60px',
                borderRadius: '5px',
                paddingLeft: '5px !important'
              }
            : undefined
        }
      />
      {((dadosUser && dadosUser.nivel !== nivelUsuarioEnum.CLIENTE) || params.row.grupoEconomico) && (
        <Box
          className={`iconsDadosEmpresaGrid${params.row.grupoEconomico ? ' flex-direction-column' : ''}`}
        >
          {dadosUser.nivel !== nivelUsuarioEnum.CLIENTE && (
            <IconButton onClick={() => montarUrlEstudoCliente(params.row.id)} size="small">
              <Avatar
                variant="rounded"
                src={EstudoClienteIcon}
                alt="Estudo cliente"
                className="iconeEmpresas"
              />
            </IconButton>
          )}
          {params.row.grupoEconomico && (
            <IconButton onClick={() => clickGrupoEconomico(params.row)} size="small">
              <Avatar
                variant="rounded"
                src={CorrenteFechadaIcon}
                alt="Grupo Econômico"
                className="iconeEmpresas"
              />
            </IconButton>
          )}
        </Box>
      )}

      <Box className="dadosEmpresaGrid margin-left-4">
        <Typography
          variant="body2"
          className="texto-nowrap-overflow-dotted camposEmpresaGrid"
          title={params.value}
        >
          {params.value}
        </Typography>

        <Box className="flex-box items-centro-verticalmente">
          <Typography
            variant="caption"
            className="texto-nowrap-overflow-dotted camposEmpresaGrid"
            title={mascararCpfCnpj(params.row.cnpjCliente)}
          >
            {mascararCpfCnpj(params.row.cnpjCliente)}
          </Typography>
          <IconButton
            size="small"
            className="btn-copiar-texto"
            onClick={() => {
              const text = params.row.cnpjCliente;
              if (
                typeof navigator !== 'undefined' &&
                navigator.clipboard &&
                typeof navigator.clipboard.writeText === 'function'
              ) {
                navigator.clipboard.writeText(text);
              } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
              }
            }}
          >
            <ContentCopyIcon className="font-size-12" fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export const criarGerenteAvatarPipeLine = params => {
  var tituloGerente = params.value;
  if (params.row && params.row.nomePlataforma)
    tituloGerente += '\nPlataf.: ' + params.row.nomePlataforma;
  if (params.row && params.row.nomeUnidadeNegocio)
    tituloGerente += '\nUnd.Neg.: ' + params.row.nomeUnidadeNegocio;
  return (
    <>
      {params.value && params.value === '-' ? (
        <HorizontalRuleIcon className="texto-cor-cinza-claro" />
      ) : (
        <Grid container title={tituloGerente}>
          <Grid item xs={12} className="flex-box-items-centralizados pad-top-5">
            <Typography
              variant="subtitle1"
              className="texto-cor-preto font-size-0-7rem texto-hidden-overflow"
            >
              {params.value}
            </Typography>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export const criarClienteNomeCnpjPipeLine = params => {
  return (
    <Grid container className="definicoes-grid-container">
      <Grid
        item
        xs={12}
        className="texto-left"
        title={params.value !== 'Total geral' ? params.value : ''}
      >
        <Typography
          className={
            params.value !== 'Total geral' ? 'texto-nowrap-overflow-dotted' : 'font-bold pad-topbot-20'
          }
        >
          {params.value}
        </Typography>
      </Grid>
      {params.row.cnpjCliente && (
        <Grid item xs={12} title={mascararCpfCnpj(params.row.cnpjCliente)}>
          <Typography
            variant="subtitle1"
            className="texto-nowrap-overflow-dotted font-size-0-8rem texto-cinza-medio"
          >
            {mascararCpfCnpj(params.row.cnpjCliente)}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export const definicaoGrupoDeColunas = ({ filtroDashboard, dadosUser, produtos }) => {
  var configGroupColumns = [
    {
      groupId: 'grp_grupoEconomico',
      headerClassName: 'data-grid-light-blue border-2-right-white',
      renderHeaderGroup: () => <></>,
      children: [{ field: 'grupoEconomico' }]
    },
    {
      groupId: 'grp_nomeCliente',
      headerClassName: 'data-grid-light-blue border-2-right-white',
      renderHeaderGroup: () => <></>,
      children: [{ field: 'nomeCliente' }]
    },
    {
      groupId: 'porteCliente',
      headerClassName: 'data-grid-light-blue border-2-right-white',
      renderHeaderGroup: () => <></>,
      children: [{ field: 'porteCliente' }]
    },
    {
      groupId: 'qtdProd',
      headerClassName: 'data-grid-light-blue border-2-right-white',
      renderHeaderGroup: () => <></>,
      children: [{ field: 'qtdProd' }]
    },
    {
      groupId: 'grp_dias_ultima_operacao',
      headerClassName: 'data-grid-light-blue border-2-right-white',
      renderHeaderGroup: () => <></>,
      children: [{ field: 'dias_ultima_operacao' }]
    }
  ];

  if (dadosUser && dadosUser.nivel !== nivelUsuarioEnum.CLIENTE) {
    configGroupColumns.unshift({
      groupId: 'grp_grupoCheckpoint',
      headerClassName: 'data-grid-light-blue border-2-right-white',
      renderHeaderGroup: () => <></>,
      children: [{ field: 'tipoStatus' }]
    });
  }

  if (filtroDashboard && filtroDashboard.gerente && filtroDashboard.gerente === 'todos') {
    configGroupColumns.unshift({
      groupId: 'grp_nomeGerente',
      headerClassName: 'data-grid-light-blue border-2-right-white',
      renderHeaderGroup: () => <></>,
      children: [{ field: 'nomeGerente' }]
    });
  }

  if (dadosUser && dadosUser.nivel === nivelUsuarioEnum.CLIENTE) {
    configGroupColumns = configGroupColumns.filter(function(el) {
      return el.groupId !== 'porteCliente';
    });
  }

  if (produtos) {
    const grupoLimCom = {
      groupId: 'grp_limite_comercial',
      headerName: 'Limite Comercial',
      headerClassName: 'data-grid-light-blue border-2-right-white font-bold-header',
      children: produtos.map(p => ({
        field: `limite_comercial_${p.siglaProduto.toLowerCase()}`
      }))
    };
    const grupoLimCred = {
      groupId: 'grp_limite_credito',
      headerName: 'Limite de Crédito',
      headerClassName: 'data-grid-light-blue border-2-right-white font-bold-header',
      children: produtos.map(p => ({
        field: `limite_credito_${p.siglaProduto.toLowerCase()}`
      }))
    };
    const grupoDisp = {
      groupId: 'grp_saldo_credito',
      headerName: 'Limite de crédito disponível',
      headerClassName: 'data-grid-light-blue border-2-right-white font-bold-header',
      children: produtos.map(p => ({
        field: `saldo_credito_${p.siglaProduto.toLowerCase()}`
      }))
    };
    const grupoRisco = {
      groupId: 'grp_risco',
      headerName: 'Risco',
      headerClassName: 'data-grid-light-blue border-2-right-white font-bold-header',
      children: produtos.map(p => ({
        field: `risco_${p.siglaProduto.toLowerCase()}`
      }))
    };
    if (dadosUser && dadosUser.nivel !== nivelUsuarioEnum.CLIENTE) {
      configGroupColumns.push(grupoLimCom, grupoLimCred, grupoDisp, grupoRisco);
    }
  }

  return configGroupColumns;
};
